const asset = require('./asset');
const isResolvedResource = require('../contentful/is-resolved-resource');
const modelTransformer = require('./model');
const moment = require('moment');
const isResolvedCollection = require('../contentful/is-resolved-collection');

const createLink = (asset, type) => {
  return {
    sys: {
      type: 'Link',
      linkType: type,
      id: asset.id
    }
  };
};

module.exports = {
  contentfulToInternal(entry) {
    const result = {
      id: entry.sys.id,
      published: Boolean(entry.sys.publishedVersion),
      updated: entry.sys.publishedVersion && entry.sys.version > entry.sys.publishedVersion + 1,
      draft: !entry.sys.publishedVersion,
      archived: Boolean(entry.sys.archivedVersion),
      contentType: null,
      created: moment(entry.sys.createdAt).format('DD.MM.YYYY HH:mm'),

      code: (entry.fields.code ? entry.fields.code['nb-NO'] : ''),
      eqId: (entry.fields.id ? entry.fields.id['nb-NO'] : ''),
      name: (entry.fields.name ? entry.fields.name['nb-NO'] : ''),
      nameCustom: (entry.fields.nameCustom ? entry.fields.nameCustom['nb-NO'] : ''),
      description: (entry.fields.description ? entry.fields.description['nb-NO'] : ''),
      descriptionCustom: (entry.fields.descriptionCustom ? entry.fields.descriptionCustom['nb-NO'] : ''),
      prices: (entry.fields.prices ? entry.fields.prices['nb-NO'] : {}),
      category: (entry.fields.category ? entry.fields.category['nb-NO'] : ''),
      priority: (entry.fields.priority ? entry.fields.priority['nb-NO'] : ''),
      tag: (entry.fields.tag ? entry.fields.tag['nb-NO'] : ''),
      price: (entry.fields.price ? entry.fields.price['nb-NO'] : ''),
      totalPrice: (entry.fields.price ? entry.fields.price['nb-NO'] : ''),
      image: null,
      model: null,
      informationFile: null,
      includedEquipment: [],
      group: (entry.fields.group ? entry.fields.group['nb-NO'] : false),

      vatOption: (entry.fields.vatOption ? entry.fields.vatOption['nb-NO'] : null),
      validVatOptions: [],

      custom: (entry.fields.custom ? entry.fields.custom['nb-NO'] : null)
    };

    if (entry.sys && entry.sys.contentType) {
      result.contentType = entry.sys.contentType.sys.id;
    }

    if (entry.fields.image && entry.fields.image['nb-NO'] && isResolvedResource(entry.fields.image['nb-NO'])) {
      result.image = asset.contentfulToInternal(entry.fields.image['nb-NO']);
    }

    // Handle model link:
    if (entry.fields.model && entry.fields.model['nb-NO'] && isResolvedResource(entry.fields.model['nb-NO'])) {
      result.model = modelTransformer.contentfulToInternal(entry.fields.model['nb-NO']);
    }

    if (entry.fields.informationFile && entry.fields.informationFile['nb-NO'] && isResolvedResource(entry.fields.informationFile['nb-NO'])) {
      result.informationFile = asset.contentfulToInternal(entry.fields.informationFile['nb-NO']);
    }

    // Get valid vat options
    const vatOptionField = (entry?.sys?.contentType?.fields ?? []).find((field) => field.id === 'vatOption');
    if (vatOptionField) {
      // Create values used in vatOption select, includes the price in the name with calculated VAT
      result.validVatOptions = vatOptionField.validations[0].in.map((option) => {
        const v = {
          name: option,
          value: option
        };

        if (entry.fields.price) {
          let totalPrice = entry.fields.price['nb-NO'];

          if (option !== 'Fritatt') {
            totalPrice = Math.ceil(totalPrice * 1.25);
          }

          v.name = `${option} (${totalPrice},-)`;
        }

        return v;
      });

      // Set default vatOption if it is missing, based on if electric or not
      if (!result.vatOption && result.model) {
        // Note: We assume vatOptionField has 0-mva first, and full-mva last
        // TODO: Use a boolean field instead? as long as we never will need any other vat options

        if (result.model.category.includes('Elbil')) {
          result.vatOption = vatOptionField.validations[0].in[0]; //'Fritatt';
        } else {
          result.vatOption = vatOptionField.validations[0].in.slice(-1)[0]; // Full mva
        }
      }

      if (result.vatOption !== 'Fritatt') {
        result.totalPrice = Math.ceil(result.price * 1.25);
      }
    }

    // Handle embedded equipment items
    // Because Contentful does not allow querying for more than one content type (even with using sys.contentType.sys.id[in]), while the
    // content model does support it, we have to split it up into one array per type in the frontend, and then combining when saving.
    if (entry.fields.includedEquipment && entry.fields.includedEquipment['nb-NO'] && isResolvedCollection(entry.fields.includedEquipment['nb-NO'])) {
      result.includedEquipment = entry.fields.includedEquipment['nb-NO'].filter(
        (resource) => resource.sys.contentType.sys.id === 'equipment'
      ).map(module.exports.contentfulToInternal);
    }


    return result;
  },

  internalToContentful(equipment) {
    const result = {
      fields: {
        descriptionCustom: {
          'nb-NO': equipment.descriptionCustom || ''
        },
        nameCustom: {
          'nb-NO': equipment.nameCustom || ''
        },
        tag: {
          'nb-NO': equipment.tag || ''
        },
        vatOption: {
          'nb-NO': equipment.vatOption || null
        },
        image: null,
        informationFile: null
      }
    };

    if (equipment.image) {
      result.fields.image = {
        'nb-NO': createLink(equipment.image, 'Asset')
      };
    }

    if (equipment.informationFile) {
      result.fields.informationFile = {
        'nb-NO': createLink(equipment.informationFile, 'Asset')
      };
    }

    if (equipment.custom) {
      result.fields.name = {
        'nb-NO': equipment.nameCustom || null
      };
      result.fields.id = {
        'nb-NO': equipment.eqId || null
      };
      result.fields.code = {
        'nb-NO': equipment.code || null
      };
      result.fields.category = {
        'nb-NO': equipment.category || null
      };
      result.fields.priority = {
        'nb-NO': equipment.priority !== undefined ? parseInt(equipment.priority, 10) : null
      };
      result.fields.price = {
        'nb-NO': equipment.price !== undefined ? parseInt(equipment.price, 10) : null
      };

      if (equipment.modelId) {
        result.fields.model = {
          'nb-NO': createLink({id: equipment.modelId}, 'Entry')
        };
      }

      result.fields.custom = {
        'nb-NO': true
      };
    }

    if (equipment.group) {
      result.fields.group = {
        'nb-NO': true
      };
    }

    if (typeof equipment.includedEquipment !== 'undefined') {
      const includedEquipments = equipment.includedEquipment || [];

      result.fields.includedEquipment = {
        'nb-NO': includedEquipments.map((item) => createLink(item, 'Entry'))
      };
    }

    return result;
  }
};
